import React from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { array, number, shape, string } from 'prop-types'

import { StarRating } from '../StarRating'
import { Thumbnail } from '../Thumbnail'
import { Link } from '@jelly/ui'

const CardContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	margin: auto;
	padding: 5rem 0 5rem 0;
	width: 90%;

	${({ theme }) => theme.MQ.S} {
		flex-direction: row;
	}
`
const ContextWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	width: 100%;

	${({ theme }) => theme.MQ.S} {
		${({ displayRatings, theme }) =>
			displayRatings &&
			`border-right: 1px solid ${theme.colors.gray[400]};
			padding-right: 1rem;
			width: 70%;`}
	}

	${({ theme }) => theme.MQ.L} {
		${({ displayRatings }) =>
			displayRatings &&
			`padding-right: 3rem;
			width: 75%;`}
	}
`
const ReviewerLink = styled(Link)`
	border-radius: 50%;
	height: fit-content;
`
const MainContext = styled.div`
	margin-left: 0;
	margin-bottom: 2rem;
	width: 80%;

	${({ theme }) => theme.MQ.S} {
		margin-left: 1rem;
		margin-bottom: 0;
		width: 90%;
	}
`
const ReviewerName = styled.div`
	${({ href, theme }) => !href && `color: ${theme.colors.gray[500]}`};
	${({ theme }) => theme.fonts.primaryMedium};
	margin-top: 2rem;
`
const Date = styled.div`
	color: ${({ theme }) => theme.colors.gray[400]};
`
const OverallRating = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.general};
	align-items: start;
	display: flex;
	flex-direction: row;
	font-size: 1.3rem;
	width: auto;
`
const StarContainer = styled.div`
	max-width: 140px;
	width: 100%;
`
const RatingsContainer = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	min-height: 450px;
	width: 100%;

	${({ theme }) => theme.MQ.S} {
		margin-left: auto;
		right: 0;
		width: 20%;
	}
`
const RatingTopic = styled.div`
	text-align: center;
`
const RatingLabel = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.primary};
	margin-bottom: 0.5rem;
`
const Description = styled.p`
	color: ${({ theme }) => theme.colors.general};
	word-break: break-word;
`

EvaluationReviewCard.propTypes = {
	description: string,
	ratings: shape({
		overall: number,
		other: array,
	}),
	reviewer: shape({
		name: string,
		image: string,
		slug: string,
	}),
	reviewDate: string,
}

/**
 * Evaluation Review Card
 * Can Display the following:
 * - Overall Rating w/ stars
 * - Category Ratings w/ stars
 * - Review Text
 * - Reviewer's name
 * - Date when the Review was published
 *
 * @param {String} props.description :: review or recommendation based on evaluation
 * @param {Object} props.ratings :: object containing different ratings based on key
 * @param {String} props.reviewer :: data of the person leaving the review or recommendation
 * @param {String} props.reviewDate :: date at which the review was published
 */
export function EvaluationReviewCard({ description, ratings, reviewer, reviewDate }) {
	const NUM_OF_STARS = 5
	const reviewerLink = reviewer?.slug ? `/advice/member/p/${reviewer.slug}/` : null
	return (
		<CardContainer>
			<ContextWrapper displayRatings={!!ratings}>
				{reviewerLink ? (
					<ReviewerLink href={reviewerLink}>
						<Thumbnail alt={reviewer?.name} src={reviewer?.image} variant="avatar" />
					</ReviewerLink>
				) : (
					<Thumbnail alt={reviewer?.name} src={reviewer?.image} variant="avatar" />
				)}

				<MainContext>
					{ratings && (
						<OverallRating>
							<StarContainer style={{ marginRight: '1.3rem' }}>
								<StarRating rating={ratings.overall} totalNumOfStars={NUM_OF_STARS} />
							</StarContainer>
							{ratings.overall}/{NUM_OF_STARS}
						</OverallRating>
					)}
					<Description>{description}</Description>
					<ReviewerName {...(reviewerLink && { as: Link, href: reviewerLink, variant: 'tertiary' })}>
						{reviewer?.name}
					</ReviewerName>
					<Date>{dayjs(reviewDate).format('MMMM DD, YYYY')}</Date>
				</MainContext>
			</ContextWrapper>
			{ratings && (
				<RatingsContainer>
					{ratings.other.map((rating, i) => {
						const { label, score } = rating
						return (
							<RatingTopic key={`rating-${i}`}>
								<RatingLabel>{label}</RatingLabel>
								<StarContainer>
									<StarRating rating={score} totalNumOfStars={NUM_OF_STARS} />
								</StarContainer>
							</RatingTopic>
						)
					})}
				</RatingsContainer>
			)}
		</CardContainer>
	)
}
